import React from 'react';

import { Botao, Chip, Modal } from '../../../ui';
import { DivergenciaFila } from '../../../api/sga-divergencia/sgaDivergenciaApi';
import { formatCpfCnpj } from '../../../utils/formartCPF';
import { formatarDataCurta } from '../../../utils/data';
import {
  rotuloOrigemDivergencia,
  rotuloStatusDivergencia,
  valorOuTraco,
} from '../../../utils/envioSgaRegras';

export interface ModalDetalhesDivergenciaProps {
  /** Divergência em exibição — null mantém o modal fechado. */
  divergencia: DivergenciaFila | null;
  ocupado: boolean;
  resolvendo: boolean;
  onFechar: () => void;
  onAceitar: (divergencia: DivergenciaFila) => void;
  onIgnorar: (divergencia: DivergenciaFila) => void;
}

/**
 * Detalhes de uma divergência local × SGA (at-32; reescrito na at-70, frente D/D8).
 *
 * O card da lista mostra o essencial para decidir — de quem é, qual campo e os dois
 * valores. A explicação por extenso (`descricao`/`orientacao`, que vêm **prontas do
 * backend**), a origem, as datas e as ações ficam aqui.
 *
 * ## O que mudou na at-70
 *
 *   1. **Virou o `Modal` do kit** (foco preso, Esc, clique fora, título `<h2>`).
 *   2. 🔴 **A prop `confirmacaoAberta` SAIU, e com ela o empilhamento.** Ela existia
 *      porque o `ModalConfirmacao` abria **por cima** deste modal e os dois
 *      escutavam `Escape`. Com o `Modal` do kit isso piora: o listener de Esc e a
 *      trava de Tab são **globais** (`window.addEventListener`), então o Tab sairia
 *      da confirmação para a página atrás. Agora a tela **fecha o detalhe antes de
 *      abrir a confirmação** — exatamente o que ela já fazia para "Ver envio", com
 *      a justificativa escrita lá: *"empilhar duas camadas de overlay deixa sem
 *      saída óbvia de volta"*. Depois de confirmar, a divergência sai da fila: não
 *      haveria o que mostrar de volta.
 *   3. **Os dois valores ganharam rótulo e moldura** ("No SAAC" / "No SGA"), com o
 *      valor local riscado — era o que o CSS `.envios-detalhe-valor` fazia.
 *   4. **Os mapas de status e origem saíram daqui** para `utils/envioSgaRegras.ts`.
 *      Eles eram exportados **deste modal** e importados pela tela — regra de
 *      negócio morando num componente, com a dependência invertida (at-24).
 *
 * ⚠️ **`rotulo`, `descricao`, `orientacao`, `gravidade` e `aplicavel` vêm prontos do
 * backend** (a whitelist vive lá). Este componente não decide o que é grave nem o
 * que exige ação manual — apenas renderiza.
 */
const ModalDetalhesDivergencia: React.FC<ModalDetalhesDivergenciaProps> = ({
  divergencia,
  ocupado,
  resolvendo,
  onFechar,
  onAceitar,
  onIgnorar,
}) => {
  if (!divergencia) return null;

  const d = divergencia;
  const emAberto = d.status === 'ABERTA';
  const status = rotuloStatusDivergencia(d.status);
  const identificacao =
    d.entidade === 'VEICULO'
      ? d.placa ?? 'Veículo'
      : d.cpfCnpj
        ? formatCpfCnpj(d.cpfCnpj)
        : 'Associado';

  return (
    <Modal
      aberto
      titulo={d.associadoNome ?? d.placa ?? 'Divergência'}
      descricao={identificacao}
      tamanho="lg"
      ocupado={resolvendo}
      aoFechar={onFechar}
    >
      <div className="tw-flex tw-flex-col tw-gap-6">
        <section>
          <h3 className="tw-m-0 tw-mb-2 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wide tw-text-texto-desabilitado">
            {d.rotulo}
          </h3>

          <div className="tw-flex tw-flex-wrap tw-gap-2">
            <Chip variante={status.variante}>{status.texto}</Chip>
            {d.gravidade === 'ALTA' && <Chip variante="perigo">Gravidade alta</Chip>}
            {!d.aplicavel && <Chip variante="neutro">Exige ação manual</Chip>}
          </div>

          <div className="tw-mt-3 tw-grid tw-gap-2 sm:tw-grid-cols-2">
            <ValorComparado rotulo="No SAAC" valor={d.valorLocal} riscado />
            <ValorComparado rotulo="No SGA" valor={d.valorSga} />
          </div>

          {/* Texto pronto do backend — nunca reescrito aqui. */}
          {d.descricao && (
            <p className="tw-m-0 tw-mt-3 tw-text-xs tw-leading-relaxed tw-text-texto-suave">
              {d.descricao}
            </p>
          )}
          {d.orientacao && (
            <p className="tw-m-0 tw-mt-1.5 tw-text-xs tw-leading-relaxed tw-text-texto-suave">
              {d.orientacao}
            </p>
          )}

          {emAberto && (
            <>
              <div className="tw-mt-3 tw-flex tw-flex-col tw-gap-2 sm:tw-flex-row">
                <Botao
                  variante="primario"
                  tamanho="sm"
                  disabled={ocupado || resolvendo}
                  onClick={() => onAceitar(d)}
                >
                  Aceitar do SGA
                </Botao>
                <Botao
                  variante="secundario"
                  tamanho="sm"
                  disabled={ocupado || resolvendo}
                  onClick={() => onIgnorar(d)}
                >
                  Ignorar
                </Botao>
              </div>

              {/* Os dois `title` dos botões, em texto. O de "aceitar" muda com
                  `aplicavel`, porque a ação é outra: gravar × só marcar resolvida. */}
              <p className="tw-m-0 tw-mt-2 tw-text-xs tw-text-texto-suave">
                {d.aplicavel
                  ? 'Aceitar grava o valor do SGA no cadastro local. Ignorar tira da fila e não volta a aparecer.'
                  : 'Este campo não é aplicado automaticamente: aceitar apenas marca como resolvida, e o ajuste é manual. Ignorar tira da fila e não volta a aparecer.'}
              </p>
            </>
          )}
        </section>

        <section>
          <h3 className="tw-m-0 tw-mb-2 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wide tw-text-texto-desabilitado">
            Detecção
          </h3>

          <div className="tw-flex tw-flex-col tw-gap-1 tw-text-xs tw-text-texto-suave">
            <span>
              Detectada em: {formatarDataCurta(d.detectadaEm)} (
              {rotuloOrigemDivergencia(d.origemDeteccao)})
            </span>
            {!emAberto && <span>Resolvida em: {formatarDataCurta(d.resolvidaEm)}</span>}
            <span>
              Registro: {d.entidade === 'VEICULO' ? 'Veículo' : 'Associado'} · campo {d.campo}
            </span>
          </div>
        </section>
      </div>
    </Modal>
  );
};

const ValorComparado: React.FC<{
  rotulo: string;
  valor: string | null;
  riscado?: boolean;
}> = ({ rotulo, valor, riscado = false }) => (
  <div className="tw-rounded-lg tw-border tw-border-superficie-borda tw-bg-superficie tw-px-3 tw-py-2">
    <div className="tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wide tw-text-texto-desabilitado">
      {rotulo}
    </div>
    <div
      className={[
        'tw-text-sm [overflow-wrap:anywhere]',
        riscado ? 'tw-text-texto-suave tw-line-through' : 'tw-font-semibold tw-text-texto-corpo',
      ].join(' ')}
    >
      {valorOuTraco(valor)}
    </div>
  </div>
);

export default ModalDetalhesDivergencia;
